import {
  Directive,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  AfterContentChecked,
  ViewContainerRef,
  EventEmitter,
  ContentChild,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';

import { timer } from 'rxjs';
import { DatatableFooterComponent } from 'ngx-datatable-footer';
import { NgxDatatableActionsComponent } from 'ngx-datatable-actions';
import { NgxQueryComponent, cloneQueryGroup, Field, QueryGroup, Rule } from '@zhongruigroup/ngx-query';
import { ColumnMode, SortType } from '@swimlane/ngx-datatable/release/types';
import { DatatableComponent } from '@swimlane/ngx-datatable/release/components';

@Directive({
  selector: '[dfNgxDataTable]',
  exportAs: 'DfNgxDataTableDirective'
})
export class NgxDataTableDirective implements OnInit, AfterViewInit, AfterContentChecked {
  @Output() data: EventEmitter<any> = new EventEmitter();

  @Input() rowHeight = 44;
  @Input() saveState = true;
  @Input() externalPaging = true;
  @Input() ngxQuery: NgxQueryComponent;
  @Input() dynamicQuery: boolean = false;
  @Input()
  set pageSize(val: number) {
    if (val < 1) {
      throw new Error(`The value '${val}' can't be less than 1.`);
    }

    this._pageSize = val;
    if (this._datatable) {
      this._datatable.limit = this._pageSize;
    }
  }
  get pageSize(): number {
    return this._pageSize;
  }
  @Input()
  set pageIndex(val: number) {
    if (val < 1) {
      throw new Error(`The value '${val}' can't be less than 1.`);
    }

    this._pageIndex = val;
    if (this._datatable) {
      this._datatable.offset = this._pageIndex;
    }
  }
  get pageIndex(): number {
    return this._pageIndex;
  }

  @ContentChild(DatatableFooterComponent) footer: DatatableFooterComponent;
  @ContentChild(NgxDatatableActionsComponent) toolbar: NgxDatatableActionsComponent;

  private latestTableWidth: number;
  private _pageSize = 10;
  private _pageIndex = 1;
  private _sorts: any[] = [];
  private _tempQueryTemplates: any;
  private _datatable: DatatableComponent;

  constructor(private router: Router, private _view: ViewContainerRef, private changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.saveState === true && !this.dynamicQuery) {
      this.loadCachePaging();
    }
    this._datatable = (<any>this._view)._data.componentView.component;
    this.initialize(this._datatable, this.ngxQuery);
  }

  ngAfterViewInit() {
    if (!this.dynamicQuery) {
      this.emitData();
    }
  }

  ngAfterContentChecked() {
    if (this._datatable && this._datatable.element.clientWidth !== this.latestTableWidth) {
      this.latestTableWidth = this._datatable.element.clientWidth;
      this._datatable.recalculate();
      this.changeDetectorRef.detectChanges();
    }
  }

  public refreshData() {
    this.emitData();
  }

  public deleteRefreshData() {
    this.emitData(undefined, undefined, 'flag');
  }

  private initialize(datatable: DatatableComponent, ngxQuery: NgxQueryComponent): void {
    // datatable
    datatable.headerHeight = 40;
    datatable.footerHeight = 40;
    datatable.scrollbarV = false;
    datatable.limit = this._pageSize;
    datatable.sortType = SortType.multi;
    datatable.rowHeight = this.rowHeight;
    datatable.offset = this._pageIndex - 1;
    datatable.columnMode = ColumnMode.force;
    datatable.externalPaging = this.externalPaging;

    if (this.footer) {
      this.footer.datatable = datatable;
    }

    if (this._sorts && this._sorts.length > 0) {
      datatable.sorts = this._sorts;
    }

    datatable.messages = {
      emptyMessage: 'å½åæ è®°å½ã',
      totalMessage: 'è¡',
      selectedMessage: 'éæ©'
    };

    datatable.page.subscribe((event: any) => {
      if (event.offset || event.offset === 0) this.emitData(event);
    });

    datatable.sort.subscribe((event: any) => {
      this.emitData(undefined, event.sorts);
    });

    // query
    if (ngxQuery) {
      ngxQuery.query.subscribe((event: any) => {
        this._datatable.offset = 0;
        this.emitData(undefined, []);
      });

      ngxQuery.reset.subscribe(() => {
        timer(100).subscribe(() => {
          this._datatable.offset = 0;
          this.emitData(undefined, []);
        });
      });
      if (this.saveState === true) {
        ngxQuery.reset.subscribe(() => {
          this.ngxQuery.queryTemplates = this._tempQueryTemplates;
        });
      }
    }
  }

  private emitData(page?: any, sorts?: any[], flag?: any) {
    // page
    if (page === undefined) {
      if (flag && this._datatable.rows && this._datatable.rows.length === 1) {
        page = {
          limit: this.pageSize,
          offset: this.pageIndex - 2
        };
      } else {
        page = {
          limit: this.pageSize,
          offset: this.pageIndex - 1
        };
      }
      this._pageIndex = page.offset + 1;
    } else {
      this._pageSize = page.limit;
      this._pageIndex = page.offset + 1;
    }
    // sort
    if (sorts === undefined) {
      sorts = this._datatable.sorts;
    } else {
      page.offset = 0;
      this._pageIndex = page.offset + 1;
    }
    const sortArray = [];
    for (const sort of sorts) {
      sortArray.push({ field: sort.prop, sort: sort.dir });
    }
    // query
    const query = this.getQuery();

    // cache
    if (this.saveState === true) {
      let rules;

      if (this.ngxQuery) {
        rules = this.ngxQuery.getOriginalQuery().rules;
      } else {
        rules = '';
      }

      const paging = {
        pageSize: page.limit,
        pageIndex: page.offset + 1 || 1,
        sorts: sorts,
        filters: query.filters,
        rules: rules
      };
      sessionStorage.setItem(this.router.url + '|saveState|dt', JSON.stringify(paging));
    }

    // event
    this.data.emit({
      datatable: this._datatable,
      // event: event,
      page: {
        pageSize: page.limit,
        pageIndex: page.offset + 1 || 1,
        sort: sortArray,
        filters: query.filters,
        filter: query.query
      },
      query: query
    });
  }

  private getQuery(): any {
    const result: any = {
      filters: []
    };
    if (this.ngxQuery) {
      let query = this.ngxQuery.getQuery();
      console.log(query, 'query');
      if (query === undefined) {
        query = this.ngxQuery.queryTemplates[0].template;
      }
      result.query = query;

      if (query && query.rules && query.rules.length > 0) {
        for (const rule of query.rules) {
          this.filterRuleData(rule);
          if (rule.op === 'bt' && rule.datas && rule.datas.length > 1) {
            if (rule.datas[0] && rule.datas[0] !== null && rule.datas[0] !== '' && rule.datas[0] !== undefined) {
              result.filters.push({
                field: rule.field,
                op: 'ge',
                term: rule.datas[0]
              });
            }
            if (rule.datas[1] && rule.datas[1] !== null && rule.datas[1] !== '' && rule.datas[1] !== undefined) {
              result.filters.push({
                field: rule.field,
                op: 'le',
                term: rule.datas[1]
              });
            }
          } else {
            if (rule.data && rule.data !== null && rule.data !== '' && rule.data !== undefined) {
              result.filters.push({
                field: rule.field,
                op: rule.op,
                term: rule.data
              });
            }
          }
        }
      }
    }

    return result;
  }

  // è¿æ»¤Ruleæ°æ®
  private filterRuleData(rule: Rule) {
    rule.data = this.filterData(rule.data);

    if (!rule.datas) {
      return;
    }

    if (rule.datas instanceof Array) {
      for (let i = 0; i < rule.datas.length; i++) {
        let data = rule.datas[i];
        data = this.filterData(data);
        rule.datas[i] = data;
      }
    }
  }

  private filterData(data: any): any {
    if (data === 0) {
      data = '0';
    }
    if (data === false) {
      data = 'false';
    }
    return data;
  }

  loadDynamicCachePaging() {
    if (this.dynamicQuery) {
      this.loadCachePaging();
    }
  }

  private loadCachePaging() {
    if (this.ngxQuery) {
      this._tempQueryTemplates = [];
      this.ngxQuery.queryTemplates.forEach((s) => {
        this._tempQueryTemplates.push({
          name: s.name,
          template: cloneQueryGroup(s.template)
        });
      });
    }

    const paging = JSON.parse(sessionStorage.getItem(this.router.url + '|saveState|dt'));

    if (paging) {
      // page
      this._pageIndex = paging.pageIndex;
      if (this._datatable) {
        this._datatable.offset = paging.pageIndex - 1;
      }
      this.pageSize = paging.pageSize;
      // query
      if (paging.filters && paging.filters.length > 0 && paging.rules) {
        if (this.ngxQuery) {
          this.resetTemplateRules(this.ngxQuery.queryTemplates[0].template, paging);
        }
      }
      // sort
      if (paging.sorts && paging.sorts.length > 0) {
        this._sorts = paging.sorts;
      }
    }
  }

  private resetTemplateRules(queryGroup: QueryGroup, paging: any) {
    // ä»¥åærulesä¸ºåï¼åªæå¼æ·è´è¿å»å°±è¡
    if (queryGroup && paging.rules) {
      if (queryGroup.rules && paging.rules) {
        const pagingRules = paging.rules;
        queryGroup.rules.forEach((s) => {
          const tempRule = pagingRules.find((tr: any) => {
            const trName = typeof tr.field === 'string' ? tr.field : (tr.field as Field).name;
            const sName = typeof s.field === 'string' ? s.field : (s.field as Field).name;
            return trName === sName;
          });
          if (tempRule) {
            s.data = tempRule.data;
            s.datas = tempRule.datas;
          } else {
            s.data = undefined;
            s.datas = undefined;
          }
        });
      }

      // if(queryGroup.groups && paging.rules.rules.groups) {
      //   this.resetTemplateRules(queryGroup.groups, paging.rules.rules.groups);
      // }
    }
  }
}
